import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Handshake,
  Envelope,
  Lock,
  PaperPlaneTilt,
  SpinnerGap,
  House,
  ArrowLeft,
  CheckCircle,
  ShieldCheck
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientLogin = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' or 'magic'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({ title: 'Please fill in all fields', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/client-portal/login`, {
        email: email.toLowerCase(),
        password
      });
      
      if (response.data.token) {
        localStorage.setItem('clientToken', response.data.token);
        toast({ title: `Welcome back, ${response.data.client_name || 'Client'}!` });
        navigate(`/client-portal?token=${response.data.token}`);
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.response?.data?.detail || 'Invalid email or password',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({ title: 'Please enter your email', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/client-portal/magic-link`, {
        email: email.toLowerCase()
      });
      
      setMagicLinkSent(true);
      toast({ title: 'Check your email!', description: 'A login link has been sent to your inbox.' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send magic link',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Check if there's already a token
  const existingToken = localStorage.getItem('clientToken');
  if (existingToken) {
    navigate(`/client-portal?token=${existingToken}`);
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B4C6F] via-[#203A55] to-[#3d6a8a] flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Back to Home */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">Back to ConstructHelp</span>
        </button>

        {/* Login Card */}
        <div className="bg-white rounded-2xl border-2 border-black shadow-[8px_8px_0px_#000] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 text-white p-6 text-center">
            <div className="w-16 h-16 bg-[#2B4C6F] rounded-xl flex items-center justify-center mx-auto mb-4 border-2 border-white/20">
              <Handshake size={36} weight="bold" />
            </div>
            <h1 className="text-2xl font-black">Client Portal</h1>
            <p className="text-white/70 mt-1">View your project progress</p>
          </div>

          {/* Content */}
          <div className="p-6">
            {magicLinkSent ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={48} className="text-green-600" weight="fill" />
                </div>
                <h2 className="text-xl font-black mb-2">Check Your Email!</h2>
                <p className="text-neutral-600 mb-6">
                  We've sent a magic link to <span className="font-bold">{email}</span>. 
                  Click the link in the email to access your project portal.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setMagicLinkSent(false)}
                  className="border-2 border-neutral-300"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Try another email
                </Button>
              </div>
            ) : (
              <>
                {/* Mode Toggle */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setMode('login')}
                    className={`flex-1 py-2 px-4 rounded-lg font-bold transition-all ${
                      mode === 'login'
                        ? 'bg-[#2B4C6F] text-white border-2 border-black shadow-[2px_2px_0px_#000]'
                        : 'bg-neutral-100 text-neutral-600 border-2 border-transparent'
                    }`}
                  >
                    Password Login
                  </button>
                  <button
                    onClick={() => setMode('magic')}
                    className={`flex-1 py-2 px-4 rounded-lg font-bold transition-all ${
                      mode === 'magic'
                        ? 'bg-[#2B4C6F] text-white border-2 border-black shadow-[2px_2px_0px_#000]'
                        : 'bg-neutral-100 text-neutral-600 border-2 border-transparent'
                    }`}
                  >
                    Magic Link
                  </button>
                </div>

                <form onSubmit={mode === 'login' ? handleLogin : handleMagicLink}>
                  {/* Email Field */}
                  <div className="mb-4">
                    <label className="block text-sm font-bold mb-2">Email Address</label>
                    <div className="relative">
                      <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="pl-10 h-12 border-2 border-neutral-300 focus:border-[#2B4C6F]"
                        required
                      />
                    </div>
                  </div>

                  {/* Password Field (only for login mode) */}
                  {mode === 'login' && (
                    <div className="mb-6">
                      <label className="block text-sm font-bold mb-2">Password</label>
                      <div className="relative">
                        <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <Input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          className="pl-10 h-12 border-2 border-neutral-300 focus:border-[#2B4C6F]"
                          required
                        />
                      </div>
                    </div>
                  )}

                  {/* Magic Link Info */}
                  {mode === 'magic' && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                      <p className="text-sm text-blue-700">
                        <strong>No password needed!</strong> We'll send a secure login link to your email.
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold text-lg border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-[2px_2px_0px_#000] transition-all"
                  >
                    {loading ? (
                      <SpinnerGap size={24} className="animate-spin" />
                    ) : mode === 'login' ? (
                      <>
                        <Lock size={20} className="mr-2" />
                        Sign In
                      </>
                    ) : (
                      <>
                        <PaperPlaneTilt size={20} className="mr-2" />
                        Send Magic Link
                      </>
                    )}
                  </Button>
                </form>

                {/* Help Text */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-neutral-500">
                    Don't have access? Ask your contractor to send you an invite link.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-neutral-50 border-t-2 border-neutral-200 px-6 py-4">
            <div className="flex items-center justify-center gap-2 text-sm text-neutral-500">
              <ShieldCheck size={18} className="text-green-600" />
              <span>Secure client portal by ConstructHelp</span>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          {[
            { icon: House, text: 'View Progress' },
            { icon: Envelope, text: 'Message Contractor' },
            { icon: CheckCircle, text: 'Approve Milestones' },
            { icon: ShieldCheck, text: 'Escrow Protection' }
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 text-white/80 text-sm">
              <item.icon size={18} />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;
